import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiAlert,
  MiButton,
  MiCheckbox,
  MiDatePicker,
  MiDetailFields,
  MiFileUpload,
  MiIcon,
  MiShowMoreList,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONTACT_CARD_EDIT_MODES,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  FILE_UPLOAD_VARIANTS,
  ICONS_WEB,
  INPUT_TYPES,
  ISSUE_STATUSES,
  PRIORITY_OPTIONS,
  TIME_PREFERENCE_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import {
  createUUID,
  getContrastColor,
  getFullName,
  isEmpty,
  toFormattedDate,
  toKebabCase,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { AddAccount } from "../../views/Accounts/AddAccount/AddAccount";
import { ACCOUNT_PAGE_TYPE } from "../../views/Accounts/accountsConstants";
import { IssueCard } from "../../views/Schedule/Scheduler/CloseoutDrawer/IssueCard/IssueCard";
import { AreaAutoComplete } from "../AreaAutoComplete/AreaAutoComplete";
import { ContactsSearch } from "../ContactsSearch/ContactsSearch";
import { Footer } from "../Footer/Footer";
import { HomeAndAccountSearch } from "../HomeAndAccountSearch/HomeAndAccountSearch";
import { MiContactCard } from "../MiContactCard/MiContactCard";
import { PreviousCustomerInfoCard } from "./PreviousCustomerInfoCard/PreviousCustomerInfoCard";
import { PurchaseOrderAutocomplete } from "./PurchaseOrderAutocomplete/PurchaseOrderAutocomplete";
import { useAssignmentCreationViewModel } from "./useAssignmentCreationViewModel";

const SECTIONS = {
  CUSTOMER: "Customer",
  REQUESTED_DATE: "Requested Date",
  ORDER_DETAILS: "Order Details",
  REPORTED_BY: "Requester",
  POINT_OF_CONTACT: "Point of Contact",
  TASKS: "Tasks",
  EPO: "EPO",
  NOTES: "Notes and Files",
};

const SHOW_MORE_LIMIT = 2;

const INITIAL_ISSUE_EDIT = {
  issueTemplateId: "",
  areaName: "",
  description: "",
  issueDocuments: [],
};

const INITIAL_EDIT = {
  accountId: "",
  reportedBy: "",
  notes: "",
  invoiceName: "",
  documents: [],
  timePreference: "",
  priority: "standard",
  ...INITIAL_ISSUE_EDIT,
};

function SubHeader({
  title,
  displayRightButton = false,
  rightButton = {},
  rightComponent = null,
  required = false,
}) {
  const theme = useTheme();
  const { classes } = useStyles();

  return (
    <Box className={classes.subheader}>
      <Box className={classes.subheaderText}>
        <MiIcon
          className={classes.accountIcon}
          color={theme.palette.common.white}
          size={0.7}
          path={mdi[ICONS_WEB.ACCOUNT_OUTLINE]}
        />
        <Typography
          fontWeight={theme.fontWeight.semiBold}
          fontSize={theme.fontSize.medium}
        >
          {title}
        </Typography>
        <Typography
          fontWeight={theme.fontWeight.semiBold}
          fontSize={theme.fontSize.medium}
          color="error.main"
        >
          {required && "*"}
        </Typography>
      </Box>
      {rightComponent}
      {displayRightButton && !rightComponent && (
        <Tooltip title={rightButton.description} placement="left" arrow>
          <Box>
            <MiButton
              className={rightButton.className}
              onClick={rightButton.onClick}
              size={rightButton.size || "medium"}
              variant={rightButton.variant || BUTTON_VARIANTS.TEXT}
              title={rightButton.description}
              startIcon={{
                path: rightButton.iconPath,
                iconColor: theme.palette.cyan.dark,
              }}
              data-testid={`${toKebabCase(rightButton.description)}-button`}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

export function AssignmentCreation({
  home = {},
  selectedOrderTemplate = {},
  onCancel = () => {},
  onSubmit = () => {},
}) {
  const { id: homeId } = home;
  const {
    id: orderTemplateId,
    name: orderTemplateName,
    abbreviation,
    fulfillmentSequence,
    color,
  } = selectedOrderTemplate;

  const [issues, setIssues] = useState([]);
  const [issueIdInEdit, setIssueIdInEdit] = useState(null);
  const [isAddingIssue, setIsAddingIssue] = useState(false);
  const [isChangingCustomer, setIsChangingCustomer] = useState(false);
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [applyToHome, setApplyToHome] = useState(false);
  const [showAddAccountDrawer, setShowAddAccountDrawer] = useState(false);
  const [searchKey, setSearchKey] = useState(0);

  const hasIssues = issues.length > 0;

  const { classes, theme } = useStyles({ color, hasIssues });

  const config = useMemo(
    () =>
      selectedOrderTemplate.fields?.reduce(
        (accu, { fieldName, visible, required }) => ({
          ...accu,
          [fieldName]: { visible, required },
        }),
        {}
      ),
    [selectedOrderTemplate]
  );
  const homeAccountIds = useMemo(
    () => [home.accountId, home.builderId].filter(Boolean),
    [home]
  );

  const validationRules = [
    {
      name: "purchaseOrderNumber",
      validationRule:
        config.orderDetails?.required &&
        z.string().min(1, "Purchase Order Number is required"),
    },
    {
      name: "notes",
      validationRule:
        config.notes?.required && z.string().min(1, "Notes is required"),
    },
  ];

  const edit = useEdit(INITIAL_EDIT, validationRules);

  const {
    isCreatingContact,
    isCreatingReportedBy,
    contacts,
    referenceIds,
    isLoading,
    issueTemplates,
    purchaseOrders,
    tenantHolidays,
    accountChanges,
    refreshReportedByCreation,
    setIsCreatingContact,
    setIsCreatingReportedBy,
    getPurchaseOrders,
    getIssueTemplates,
    getHomeInfo,
    getTenantHolidays,
    updateHomeAccount,
    createAssignment,
  } = useAssignmentCreationViewModel({
    homeId,
    selectedOrderTemplate,
    config,
    edit,
    issues,
    onSubmit,
  });

  const submitDisabled =
    isLoading ||
    !edit.isValid() ||
    isAddingIssue ||
    issueIdInEdit ||
    (config.accountId?.required && !edit.getValue("accountId")) ||
    (config.requestedDate?.required && !edit.getValue("requestedStartDate")) ||
    (config.orderDetails?.required && !edit.getValue("purchaseOrderNumber")) ||
    (config.recipientId?.required &&
      !contacts?.some(({ isPrimary }) => isPrimary)) ||
    (config.requesterId?.required && !edit.getValue("reportedBy")?.id) ||
    (config.issue?.required && isEmpty(issues)) ||
    (config.notes?.required && !edit.getValue("notes")) ||
    (config.invoiceName?.required && !edit.getValue("invoiceName")) ||
    (config.documents?.required && isEmpty(edit.getValue("documents")));

  const issueSaveDisabled = !edit.allFilled(
    "issueTemplateId",
    "areaName",
    "description"
  );

  const reset = () => {
    getHomeInfo();
    getIssueTemplates();
    getPurchaseOrders();
    getTenantHolidays();
    setIssues([]);
    setIsCreatingContact(false);
    setIsCreatingReportedBy(false);
    setIssueIdInEdit(null);
    setIsAddingIssue(false);
    setIsChangingCustomer(false);
    setPendingCustomer(null);
    setSelectedCustomer(null);
    setApplyToHome(false);
    edit.update(INITIAL_EDIT);
  };

  useEffect(() => {
    reset();
  }, [homeId, orderTemplateId]);

  const onPurchaseOrderChange = (purchaseOrder = {}) => {
    edit.update({
      purchaseOrderNumber: purchaseOrder.number || "",
      purchaseOrderId: purchaseOrder.id || "",
    });
  };

  const onPurchaseOrderCreate = (purchaseOrder = {}) => {
    onPurchaseOrderChange(purchaseOrder);
    getPurchaseOrders();
  };

  const requestedStartDate = edit.getValue("requestedStartDate");
  const holidayMatch = useMemo(() => {
    if (!requestedStartDate || !tenantHolidays?.length) return null;
    const orderDate = moment(requestedStartDate);
    return (
      tenantHolidays.find((holiday) => {
        const start = moment(holiday.startDate).startOf("day");
        const end = moment(holiday.endDate).endOf("day");
        return orderDate.isBetween(start, end, undefined, "[]");
      }) || null
    );
  }, [requestedStartDate, tenantHolidays]);

  const detailFields = [
    {
      // 0
      label: "Requested Date",
      fieldType: INPUT_TYPES.CUSTOM,
      name: "requestedStartDate",
      customComponent: (
        <MiDatePicker
          isFullWidth
          required={config.requestedDate?.required}
          label="Requested Date"
          onChange={(value) => {
            edit.update({
              requestedStartDate: toFormattedDate(value),
            });
          }}
          variant={DATEPICKER_VARIANTS.DESKTOP}
          actions={[DATEPICKER_ACTIONS.CANCEL]}
        />
      ),
      width: "30%",
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      // 1
      label: "Time Preference",
      name: "timePreference",
      fieldType: INPUT_TYPES.SELECT,
      options: TIME_PREFERENCE_OPTIONS,
      style: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      handleChange: ({ target: { value } }) => {
        edit.update({ timePreference: value });
      },
      width: "30%",
    },
    {
      // 2
      label: "Priority",
      name: "priority",
      fieldType: INPUT_TYPES.SELECT,
      options: PRIORITY_OPTIONS,
      required: config?.priority?.required,
      style: { paddingLeft: 0, paddingRight: 0 },
      width: "30%",
    },
    {
      // 3
      label: "Job Number",
      name: "jobNumber",
      padding: 0,
    },
    {
      // 4
      label: "Purchase Order Number",
      name: "purchaseOrderNumber",
      fieldType: INPUT_TYPES.CUSTOM,
      padding: 0,
      customComponent: (
        <PurchaseOrderAutocomplete
          homeId={homeId}
          required={config.orderDetails?.required}
          orderTemplate={selectedOrderTemplate}
          value={edit.getValue("purchaseOrderNumber")}
          purchaseOrders={purchaseOrders}
          onChange={onPurchaseOrderChange}
          onCreate={onPurchaseOrderCreate}
        />
      ),
    },
    {
      // 5
      label: "Requester",
      name: "reportedBy",
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      freeSolo: true,
      options:
        contacts?.map((contact) => ({
          label: `${getFullName(contact.firstName, contact.lastName)} | ${
            contact.label
          }`,
          value: contact,
        })) || [],
      onInputChange: (_, value) => {
        edit.update({ reportedBy: value });
      },
      handleChange: (_, option) =>
        option && edit.update({ reportedBy: option.value }),
      style: { padding: 0, flex: 1 },
      required: config.requesterId?.required,
    },
    {
      // 6
      label: "Task Title",
      fieldType: INPUT_TYPES.SELECT,
      name: "issueTemplateId",
      options: issueTemplates,
      required: config.issue?.required,
      padding: 0,
    },
    {
      // 7
      label: "Area",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <AreaAutoComplete
          onChange={(value) => {
            edit.update({ areaName: value });
          }}
        />
      ),
      name: "areaName",
      width: "100%",
      required: true,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      // 8
      label: "Description",
      name: "description",
      multiline: true,
      required: config.issue?.required,
      rows: 2,
      width: "100%",
      padding: 0,
    },
    {
      // 9
      label: "EPO - Invoice Name",
      name: "invoiceName",
      multiline: true,
      required: config.invoiceName?.required,
      rows: 1,
      width: "100%",
      padding: 0,
    },
    {
      // 10
      label: "Notes",
      name: "notes",
      multiline: true,
      rows: 2,
      required: config.notes?.required,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
  ];

  const getDetailFields = (title) => {
    switch (title) {
      case SECTIONS.REQUESTED_DATE:
        return detailFields.slice(0, 3);
      case SECTIONS.ORDER_DETAILS:
        return detailFields.slice(3, 5);
      case SECTIONS.POINT_OF_CONTACT:
        return detailFields.slice(5, 6);
      case SECTIONS.REPORTED_BY:
        return detailFields.slice(5, 6);
      case SECTIONS.TASKS:
        return detailFields.slice(6, 9);
      case SECTIONS.EPO:
        return detailFields.slice(9, 10);
      case SECTIONS.NOTES:
        return detailFields.slice(10);
      default:
        return [];
    }
  };

  const handleClearIssue = () => {
    setIsAddingIssue(false);
    setIssueIdInEdit(null);

    edit.update(INITIAL_ISSUE_EDIT);
  };

  const getIssueInfo = () => {
    const issueTitle = issueTemplates.find(
      ({ value }) => edit.getValue("issueTemplateId") === value
    )?.label;

    return {
      issueTemplateName: issueTitle,
      title: issueTitle,
      issueTemplateId: edit.getValue("issueTemplateId"),
      areaName: edit.getValue("areaName"),
      description: edit.getValue("description"),
      issueDocuments: edit.getValue("issueDocuments"),
      status: ISSUE_STATUSES.NOT_CREATED,
    };
  };

  const handleAddIssue = () => {
    setIssues((prev) => [
      ...prev,
      {
        id: createUUID(),
        ...getIssueInfo(),
      },
    ]);

    handleClearIssue();
  };

  const handleDeleteIssue = (issueId) => () => {
    setIssues((prev) => prev.filter((issue) => issue.id !== issueId));
  };

  const handleSaveCustomerChange = async () => {
    if (!pendingCustomer) {
      return;
    }

    setSelectedCustomer(pendingCustomer);
    edit.update({ accountId: pendingCustomer.id });
    setIsChangingCustomer(false);

    if (applyToHome) {
      await updateHomeAccount(pendingCustomer.id);
    }

    setPendingCustomer(null);
    setApplyToHome(false);
  };

  const handleSaveIssue = () => {
    setIssues((prev) =>
      prev.map((issue) =>
        issue.id === issueIdInEdit
          ? {
              ...issue,
              ...getIssueInfo(),
            }
          : issue
      )
    );

    handleClearIssue();
    setIssueIdInEdit(null);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.scrollArea}>
        <Box className={classes.homeOwnerRequestContainer}>
          {config.accountId?.visible && (
            <Box className={classes.homeOwnerRequestContent}>
              <Box
                data-testid={toKebabCase(SECTIONS.CUSTOMER)}
                className={classes.containerStyle}
              >
                <SubHeader
                  title={SECTIONS.CUSTOMER}
                  required={config.accountId?.required}
                  displayRightButton={!isChangingCustomer}
                  rightButton={{
                    description: "Change Customer",
                    variant: BUTTON_VARIANTS.OUTLINED,
                    size: "small",
                    onClick: () => setIsChangingCustomer(true),
                  }}
                  rightComponent={
                    isChangingCustomer ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <MiButton
                          title="Cancel"
                          variant={BUTTON_VARIANTS.OUTLINED}
                          color={BUTTON_COLORS.BLACK}
                          onClick={() => {
                            setIsChangingCustomer(false);
                            setPendingCustomer(null);
                          }}
                          size="small"
                        />
                        <MiButton
                          title="Save"
                          variant={BUTTON_VARIANTS.CONTAINED}
                          color={BUTTON_COLORS.ORANGE}
                          disabled={!pendingCustomer}
                          onClick={handleSaveCustomerChange}
                          size="small"
                        />
                      </Box>
                    ) : null
                  }
                />
                {isChangingCustomer ? (
                  <>
                    <HomeAndAccountSearch
                      key={searchKey}
                      type="account"
                      required={config.accountId?.required}
                      onCreate={() => setShowAddAccountDrawer(true)}
                      onSelect={(account) => {
                        setPendingCustomer(account);
                      }}
                      onReset={() => {
                        setPendingCustomer(null);
                      }}
                    />
                    <Typography>Apply Change To</Typography>
                    <Box>
                      <MiCheckbox
                        label="Home"
                        style={{ padding: 0 }}
                        value={applyToHome}
                        handleChange={() => setApplyToHome((prev) => !prev)}
                      />
                    </Box>
                  </>
                ) : (
                  <Box className={classes.customerFieldContainer}>
                    <TextField
                      label="Customer"
                      value={selectedCustomer?.name || home.accountName || ""}
                      fullWidth
                      size="small"
                      disabled
                      slotProps={{
                        input: {
                          style: {
                            backgroundColor: theme.palette.common.white,
                          },
                        },
                      }}
                    />
                    {selectedCustomer && home.accountName && (
                      <Tooltip
                        title={
                          <PreviousCustomerInfoCard
                            accountChanges={accountChanges}
                          />
                        }
                        slotProps={{
                          popper: {
                            placement: "right",
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [0, -18] },
                              },
                            ],
                            className: classes.previousCustomerTooltipPopper,
                          },
                          tooltip: {
                            className: classes.previousCustomerTooltip,
                          },
                        }}
                      >
                        <Box className={classes.previousCustomerContainer}>
                          <Typography
                            fontSize={theme.fontSize.small}
                            color={theme.palette.text.secondary}
                          >
                            Previous Customer
                          </Typography>
                          <Typography
                            fontWeight={theme.fontWeight.semiBold}
                            fontSize={theme.fontSize.medium}
                          >
                            {home.accountName}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          <Box className={classes.homeOwnerRequestContent}>
            <Box className={classes.header}>
              <Box className={classes.abbreviationContainer}>
                <Typography
                  fontSize={theme.fontSize.xsmall}
                  color={getContrastColor(color)}
                >
                  {abbreviation}
                </Typography>
              </Box>
              <Typography fontWeight={theme.fontWeight.semiBold}>
                {fulfillmentSequence ? `${fulfillmentSequence}.` : ""}
                {orderTemplateName}
              </Typography>
            </Box>

            {config.requestedDate?.visible && (
              <Box
                data-testid={toKebabCase(SECTIONS.REQUESTED_DATE)}
                className={classes.containerStyle}
              >
                <SubHeader
                  title={SECTIONS.REQUESTED_DATE}
                  required={config.requestedDate?.required}
                />
                <MiDetailFields
                  edit={edit}
                  detailFields={getDetailFields(SECTIONS.REQUESTED_DATE)}
                  customWrapperStyle={classes.detailFieldsStyle}
                />
                {holidayMatch && (
                  <MiAlert
                    key={requestedStartDate}
                    visible
                    variant="warning"
                    title="Requested Date is during a holiday"
                    description="Creating this order for this date requires manager approval."
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
            {config.orderDetails?.visible && (
              <Box
                data-testid={toKebabCase(SECTIONS.ORDER_DETAILS)}
                className={classes.containerStyle}
              >
                <SubHeader
                  title={SECTIONS.ORDER_DETAILS}
                  required={config.orderDetails?.required}
                />
                <MiDetailFields
                  edit={edit}
                  detailFields={getDetailFields(SECTIONS.ORDER_DETAILS)}
                  customWrapperStyle={classes.detailFieldsStyle}
                />
              </Box>
            )}
            <Box className={classes.contactContainer}>
              {config.recipientId?.visible && (
                <Box
                  className={[classes.contact, classes.pointOfContact]}
                  data-testid={toKebabCase(SECTIONS.POINT_OF_CONTACT)}
                >
                  <SubHeader
                    displayRightButton
                    required={config.recipientId?.required}
                    title={SECTIONS.POINT_OF_CONTACT}
                    rightButton={
                      isCreatingContact
                        ? {
                            description: "Cancel",
                            className: classes.addButton,
                            onClick: () => setIsCreatingContact(false),
                          }
                        : {
                            description: "Add Contact",
                            iconPath: mdi[ICONS_WEB.PLUS],
                            onClick: () => setIsCreatingContact(true),
                            className: classes.addButton,
                          }
                    }
                  />
                  {isCreatingContact ? (
                    <ContactsSearch
                      accountIds={homeAccountIds}
                      placeholder="Search All Contacts"
                      referenceIds={referenceIds}
                      onRefresh={() => {
                        getHomeInfo();
                        setIsCreatingContact(false);
                      }}
                    />
                  ) : (
                    <MiShowMoreList
                      listContainerStyle={classes.listPointsOfContact}
                      enableShowMore
                      showMoreLimit={SHOW_MORE_LIMIT}
                    >
                      {contacts.length > 0 ? (
                        contacts.map((contact) => (
                          <MiContactCard
                            key={contact.id}
                            contact={contact}
                            editMode={[
                              CONTACT_CARD_EDIT_MODES.RELATIONSHIP,
                              CONTACT_CARD_EDIT_MODES.CONTACT,
                            ]}
                            defaultContactLevel={contact.level}
                            onRefresh={getHomeInfo}
                            referenceIds={referenceIds}
                          />
                        ))
                      ) : (
                        <Typography
                          color={theme.palette.grey.medium}
                          fontSize={theme.fontSize.medium}
                          textAlign="center"
                        >
                          Select “Add Contact” to start adding a point of
                          contact for this order.
                        </Typography>
                      )}
                    </MiShowMoreList>
                  )}
                </Box>
              )}
              {config.requesterId?.visible && (
                <Box
                  className={classes.contact}
                  data-testid={toKebabCase(SECTIONS.REPORTED_BY)}
                >
                  <SubHeader
                    title={SECTIONS.REPORTED_BY}
                    displayRightButton
                    rightButton={(() => {
                      if (isCreatingReportedBy) {
                        return {
                          description: "Cancel",
                          className: classes.addButton,
                          onClick: () => setIsCreatingReportedBy(false),
                        };
                      }

                      if (edit.getValue("reportedBy")?.id) {
                        return {
                          description: "Change Contact",
                          iconPath: mdi[ICONS_WEB.PENCIL],
                          className: classes.addButton,
                          onClick: () => {
                            edit.update({
                              reportedBy: getFullName(
                                edit.getValue("reportedBy").firstName,
                                edit.getValue("reportedBy").lastName
                              ),
                            });
                          },
                        };
                      }

                      return {
                        description: "Add Contact",
                        iconPath: mdi[ICONS_WEB.PLUS],
                        className: classes.addButton,
                        onClick: () => {
                          setIsCreatingReportedBy(true);
                        },
                      };
                    })()}
                    required={config.requesterId?.required}
                  />
                  {isCreatingReportedBy && (
                    <ContactsSearch
                      placeholder="Search All Contacts"
                      accountTypes={[AccountType.Builder, AccountType.Service]}
                      referenceIds={referenceIds}
                      onRefresh={refreshReportedByCreation}
                    />
                  )}
                  {edit.getValue("reportedBy")?.id ? (
                    <MiContactCard
                      key={edit.getValue("reportedBy").id}
                      contact={edit.getValue("reportedBy")}
                    />
                  ) : (
                    !isCreatingReportedBy &&
                    contacts.length > 0 && (
                      <MiDetailFields
                        edit={edit}
                        detailFields={getDetailFields(SECTIONS.REPORTED_BY)}
                      />
                    )
                  )}
                  {!isCreatingReportedBy && contacts.length === 0 && (
                    <Typography
                      color={theme.palette.grey.medium}
                      fontSize={theme.fontSize.medium}
                      textAlign="center"
                    >
                      Select “Add Contact” to start adding a requester for this
                      order.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            {config.issue?.visible && (
              <Box data-testid={toKebabCase(SECTIONS.TASKS)}>
                <SubHeader
                  title={SECTIONS.TASKS}
                  required={config.issue?.required}
                />
                {issues
                  .filter(({ id }) => id !== issueIdInEdit)
                  .map((issue, i) => (
                    <IssueCard
                      key={issue.id}
                      testId={`issue-card-${issue.id}`}
                      issueIndex={i + 1}
                      issue={issue}
                      customClass={classes.issueCard}
                      iconBgColor={theme.palette.grey.medium}
                      handleEdit={() => {
                        if (issue.id) {
                          setIssueIdInEdit(issue.id);
                          edit.update(issue);
                        }
                      }}
                      handleDelete={handleDeleteIssue(issue.id)}
                    />
                  ))}
                {(isAddingIssue || issueIdInEdit) && (
                  <Box className={classes.issueForm}>
                    <MiDetailFields
                      edit={edit}
                      customWrapperStyle={classes.issueEditContainer}
                      detailFields={getDetailFields(SECTIONS.TASKS)}
                    />
                    <MiFileUpload
                      variant={FILE_UPLOAD_VARIANTS.BUTTON}
                      onChange={(e) => {
                        edit.update({ issueDocuments: e });
                      }}
                      initialFiles={edit.getValue("issueDocuments")}
                      style={{ padding: 0, marginTop: theme.spacing(2) }}
                      testId="issue-documents"
                      buttonClassName={classes.uploadFileButton}
                      buttonStartIcon={{
                        path: mdi[ICONS_WEB.PLUS_CIRCLE],
                        iconColor: theme.palette.cyan.dark,
                        iconSize: 0.7,
                      }}
                      title="Upload files"
                    />
                    <Box className={classes.issueFooter}>
                      <MiButton
                        className={classes.cancelBtn}
                        title="Cancel"
                        variant={BUTTON_VARIANTS.TEXT}
                        onClick={handleClearIssue}
                        size="small"
                      />
                      <MiButton
                        className={classes.confirmBtn}
                        title={issueIdInEdit ? "Save Task" : "Create Task"}
                        variant={BUTTON_VARIANTS.CONTAINED}
                        disabled={issueSaveDisabled}
                        onClick={() => {
                          if (issueIdInEdit) {
                            handleSaveIssue();
                          } else {
                            handleAddIssue();
                          }
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
                {!isAddingIssue && !issueIdInEdit && (
                  <MiButton
                    title={issues.length > 0 ? "Add New Task" : "Create Task"}
                    startIcon={{
                      path: hasIssues
                        ? mdi[ICONS_WEB.PLUS_CIRCLE]
                        : mdi[ICONS_WEB.PLUS],
                      iconColor: hasIssues
                        ? theme.palette.cyan.dark
                        : theme.palette.white.main,
                    }}
                    color={hasIssues ? "white" : "primary"}
                    variant="contained"
                    className={classes.addIssueButton}
                    onClick={() => {
                      setIsAddingIssue(true);
                    }}
                  />
                )}
              </Box>
            )}
            {config.invoiceName?.visible && (
              <Box
                data-testid={toKebabCase(SECTIONS.EPO)}
                className={classes.containerStyle}
              >
                <SubHeader
                  title={SECTIONS.EPO}
                  required={config.invoiceName?.required}
                />
                <MiDetailFields
                  edit={edit}
                  detailFields={getDetailFields(SECTIONS.EPO)}
                />
              </Box>
            )}
            {(config.notes?.visible || config.documents?.visible) && (
              <Box data-testid={toKebabCase(SECTIONS.NOTES)}>
                <SubHeader
                  title={SECTIONS.NOTES}
                  required={
                    config.notes?.required || config.documents?.required
                  }
                />
                {config.notes?.visible && (
                  <MiDetailFields
                    edit={edit}
                    detailFields={getDetailFields(SECTIONS.NOTES)}
                  />
                )}
                {config.documents?.visible && (
                  <MiFileUpload
                    dragAndDrop
                    onChange={(e) => {
                      edit.update({ documents: e });
                    }}
                    testId="documents"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {showAddAccountDrawer && (
        <AddAccount
          type={ACCOUNT_PAGE_TYPE.CUSTOMER}
          showDrawer={showAddAccountDrawer}
          onCloseDrawer={() => setShowAddAccountDrawer(false)}
          onRefresh={() => setSearchKey((k) => k + 1)}
          isAccountTypeEditable
        />
      )}
      <Footer
        show
        height={50}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: onCancel,
          },
          {
            name: "Save and Create Order",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: createAssignment,
            disabled: submitDisabled,
          },
        ]}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme, { color, hasIssues } = {}) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  scrollArea: {
    flex: 1,
    overflow: "auto",
  },
  homeOwnerRequestContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2.5),
  },
  homeOwnerRequestContent: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2, 4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.mediumLight}`,
  },
  detailFieldsStyle: {
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "row",
  },
  containerStyle: {
    gap: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  pointOfContact: {
    [theme.breakpoints.up("lg")]: {
      marginRight: theme.spacing(2),
    },
  },
  header: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(4, 0, 1),
  },
  listPointsOfContact: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 0, 0.5, 0),
  },
  uploadFileButton: {
    width: "100%",
    border: `2px dashed ${theme.palette.grey.darkAccent}`,
    "&:hover": {
      border: `2px dashed ${theme.palette.grey.darkAccent}`,
    },
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
  abbreviationContainer: {
    backgroundColor: color,
    borderRadius: theme.spacing(0.5),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 20,
    minWidth: 20,
    width: "fit-content",
    padding: theme.spacing(0, 1),
  },
  accountIcon: {
    backgroundColor: theme.palette.cyan.dark,
    borderRadius: "100%",
    padding: theme.spacing(1),
  },
  subheaderText: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  contactContainer: {
    display: "flex",
  },
  customerFieldContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  previousCustomerTooltipPopper: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
  },
  previousCustomerTooltip: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
  },
  previousCustomerContainer: {
    display: "flex",
    minWidth: theme.spacing(15),
    flexDirection: "column",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  issueForm: {
    backgroundColor: theme.palette.common.white,
    border: `${theme.spacing(0.25)} solid ${theme.palette.cyan.dark}`,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  contact: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  subheader: {
    display: "flex",
    justifyContent: "space-between",
  },
  addButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.cyan.dark,
  },
  addIssueButton: {
    width: "100%",
    height: 42,
    backgroundColor: hasIssues
      ? theme.palette.common.white
      : theme.palette.cyan.dark,
    "&:hover": {
      backgroundColor: hasIssues
        ? theme.palette.common.white
        : theme.palette.cyan.dark,
    },
    color: hasIssues ? theme.palette.cyan.dark : theme.palette.common.white,
    margin: `${theme.spacing(1)} 0px`,
    fontWeight: hasIssues ? theme.fontWeight.semiBold : theme.fontWeight.normal,
    boxShadow: `0px ${theme.spacing(0.5)} ${theme.spacing(0.5)} 0px ${
      theme.palette.grey.darkAccent
    }`,
  },
  issueFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    paddingTop: theme.spacing(1),
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: "underline",
    textUnderlineOffset: theme.spacing(1),
  },
  confirmBtn: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  issueEditContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  issueCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1],
    border: `${theme.spacing(0.5)} solid ${theme.palette.grey.light}`,
  },
  issueCardButton: {
    height: "fit-content",
    padding: theme.spacing(1),
    borderRadius: "100%",
  },
}));
